// src/services/tenantVerificationService.js - Fixed to avoid unnecessary API call
import { businessCentralScopes } from '../auth/authConfig';
import logger from '../utils/logger';

class TenantVerificationService {
  constructor(getAccessToken) {
    this.getAccessToken = getAccessToken;
    this.backendApiUrl = process.env.REACT_APP_API_BASE_URL || "https://demo-backend-demo.uksouth-01.azurewebsites.net/api";
    this.registrationId = null; // Store registrationId for environments filtering
  }

  /**
   * Verify user access to BC portal
   * @param {string} email - User's email address 
   * @param {string} userId - User's ID/OID
   * @returns {Promise<object>} Verification result
   */
  async verifyUserAccess(email, userId) {
    try {
      // Get registrationId from localStorage if available
      const storedRegistrationId = localStorage.getItem('registrationId');
      if (storedRegistrationId) {
        this.registrationId = storedRegistrationId;
        logger.log('Using registrationId from localStorage:', this.registrationId);
      }
      
      // Get token for BC API
      const token = await this.getAccessToken(businessCentralScopes.read);
      if (!token || token === 'Not Readable' || token === 'mock-token-for-development') {
        throw new Error('Invalid authentication token. Please log in again.');
      }
      
      // Now use the portalUsers/verify endpoint instead of auth/verify
      const response = await fetch(`${this.backendApiUrl}/bc/portalUsers/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email, userId })
      });

      // Handle 404 (user not found) responses
      if (response.status === 404) {
        logger.log('User not found in BC portal, will redirect to landing page');
        const errorData = await response.json();
        
        // Clean up session data
        this.cleanupSession();
        
        return {
          exists: false,
          isActive: false,
          message: errorData.message || 'User not found in Business Central Portal',
          requiresRedirect: true
        };
      }

      if (!response.ok) {
        const error = await response.json();
        // Check if server indicates we should clean up
        if (error.shouldCleanup) {
          this.cleanupSession();
          return {
            exists: false,
            isActive: false,
            message: error.message || "Failed to verify user access",
            requiresRedirect: true
          };
        }
        throw new Error(error.message || "Failed to verify user access");
      }

      const userData = await response.json();
      
      // If the user doesn't exist, we still need to clean up
      if (userData && (userData.exists === false || userData.isActive === false)) {
        logger.log('User verification failed, cleaning up session data');
        this.cleanupSession();
        
        // Add redirect flag
        userData.requiresRedirect = true;
        return userData;
      }
      
      // Store the registrationId for later use when fetching environments
      if (userData && userData.userData && userData.userData.registrationId) {
        this.registrationId = userData.userData.registrationId;
        localStorage.setItem('registrationId', userData.userData.registrationId);
        logger.log('Retrieved registrationId from user verification:', this.registrationId);
      }
      
      return userData;
    } catch (error) {
      logger.error("Error verifying user access:", error);
      
      // For development mode, return mock success data
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Returning mock user verification data');
        
        const mockData = {
          exists: true,
          isActive: true,
          message: 'User access verified (mock)',
          userData: {
            id: 'mock-user-id',
            name: email.split('@')[0] || 'User',
            email: email,
            role: 'Employee',
            status: 'Active',
            registrationId: 'mock-registration-id'
          }
        };
        
        // Store registration ID for environments
        this.registrationId = mockData.userData.registrationId;
        localStorage.setItem('registrationId', mockData.userData.registrationId);
        
        return mockData;
      }
      
      // Any error in verifying user access should also clean up session
      this.cleanupSession();
      
      return {
        exists: false,
        isActive: false,
        message: error.message || "Error verifying user access",
        requiresRedirect: true
      };
    }
  }
  
  /**
   * Get available environments for verified user
   * @returns {Promise<object>} Available environments
   */
  async getAvailableEnvironments() {
    try {
      // Get token for BC API
      const token = await this.getAccessToken(businessCentralScopes.read);
      if (!token || token === 'Not Readable' || token === 'mock-token-for-development') {
        throw new Error('Invalid authentication token. Please log in again.');
      }
      
      // Build the proper URL with filter if we have a registrationId
      let url = `${this.backendApiUrl}/bc/environments`;
      
      if (this.registrationId) {
        // Add filter for registrationId if available
        const filter = encodeURIComponent(`registrationId eq '${this.registrationId}'`);
        url = `${this.backendApiUrl}/bc/environments?$filter=${filter}`;
        logger.log('Fetching environments with registrationId filter:', url);
      } else {
        logger.warn('No registrationId available for filtering environments');
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to retrieve environments");
      }

      const data = await response.json();
      
      // Normalize the response format
      // The API might return data.value for OData or data.environments for our custom endpoint
      const environments = data.value || data.environments || [];
      
      // Transform to expected format if necessary
      const formattedEnvironments = environments.map(env => ({
        id: env.id || env.environmentId,
        name: env.environmentName || env.name,
        type: env.environmentType || env.type || 'Production',
        url: env.environmentUrl || env.url || '',
        status: env.status || 'Active',
        companyName: env.companyName || env.company_Name || '',
        company_Id: env.company_Id || env.companyId || ''
      }));
      
      return { environments: formattedEnvironments };
    } catch (error) {
      logger.error("Error fetching environments:", error);
      
      // For development mode, return mock data
      if (process.env.NODE_ENV === 'development') {
        logger.log('Development mode: Returning mock environments');
        
        return { 
          environments: [
            {
              id: 'env-production-01',
              name: 'Production',
              type: 'Production',
              url: 'https://businesscentral.dynamics.com/production',
              status: 'Active'
            },
            {
              id: 'env-sandbox-01',
              name: 'Sandbox',
              type: 'Sandbox',
              url: 'https://businesscentral.dynamics.com/sandbox',
              status: 'Active'
            }
          ]
        };
      }
      
      // Return empty array to avoid errors
      return { environments: [] }; 
    }
  }

  /**
   * Set active environment for the session - this just updates localStorage
   * and does NOT call any backend API to avoid the infinite loop issue
   * @param {string} environmentId - Environment ID
   * @param {string} environmentName - Environment name
   * @returns {Promise<object>} Result
   */
  async setActiveEnvironment(environmentId, environmentName) {
    try {
      // Store environment info in localStorage
      const environmentData = {
        id: environmentId,
        name: environmentName,
        // Include additional properties as needed
        activatedAt: new Date().toISOString()
      };
      
      localStorage.setItem('activeEnvironment', JSON.stringify(environmentData));
      logger.log('Environment stored in localStorage:', environmentData);
      
      // Return success without making any API call
      return {
        success: true,
        environment: environmentData,
        message: 'Environment activated successfully'
      };
    } catch (error) {
      logger.error("Error setting active environment:", error);
      throw error;
    }
  }
  
  /**
   * Helper method to clean up session data
   */
  cleanupSession() {
    logger.log('Cleaning up session data');
    
    // Clear localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('activeEnvironment');
    localStorage.removeItem('registrationId');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('employeeNo');
    localStorage.removeItem('employeeId');
    localStorage.removeItem('clientCompanyId');
    localStorage.removeItem('clientCompanyName');
    localStorage.removeItem('clientCompanies');
    
    // Clear sessionStorage (MSAL tokens)
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('msal.') || key.includes('token') || key.includes('auth')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Clear cookies related to authentication
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      if (name.includes('msal') || name.includes('auth') || name.includes('session')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
  }
}

export default TenantVerificationService;